import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { ClipboardList, Search, AlertTriangle, Wrench, X } from 'lucide-react';
import BillingDetailSheet from '../../components/BillingDetailSheet';
import BillingCreateSheet from '../../components/BillingCreateSheet';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import CustomerTabsBar from '../../components/CustomerTabsBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TZ = 'America/New_York';

const WORKFLOW = [
  { key: 'draft', label: 'Draft' },
  { key: 'needs_review', label: 'Needs Review' },
  { key: 'ready_to_send', label: 'Ready to Send' },
  { key: 'sent', label: 'Sent' },
  { key: 'viewed', label: 'Viewed' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'declined', label: 'Declined' },
  { key: 'on_hold', label: 'On Hold' },
  { key: 'expired', label: 'Expired' },
  { key: 'converted', label: 'Converted to Invoice' },
  { key: 'voided', label: 'Voided' },
];
const REASON_REQUIRED = new Set(['declined', 'voided']);

// ET calendar date (YYYY-MM-DD) for a value
const etDate = (v) => {
  if (!v) return '';
  try {
    const d = typeof v === 'string' && v.length === 10 ? new Date(v + 'T12:00:00') : new Date(v);
    return new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  } catch { return String(v).slice(0, 10); }
};
const todayET = () => etDate(new Date());

const AdminEstimates = () => {
  const { hasPermission, user } = useAuth();
  const canEdit = hasPermission('edit_estimates');
  const [estimates, setEstimates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editOnOpen, setEditOnOpen] = useState(false);
  const [tab, setTab] = useState('all');
  const [range, setRange] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [q, setQ] = useState('');
  const [techFilter, setTechFilter] = useState('all');
  const [diag, setDiag] = useState(null);
  const [showDiag, setShowDiag] = useState(false);
  const [audit, setAudit] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => { refresh(); }, []);

  // Deep-link open/edit from the Field View "Estimate Group" (mirrors AdminInvoices).
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId || estimates.length === 0) return;
    const target = estimates.find((e) => e.id === openId);
    if (target) {
      setSelected(target);
      setEditOnOpen(searchParams.get('edit') === '1' && canEdit);
      const next = new URLSearchParams(searchParams);
      next.delete('open');
      next.delete('edit');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, estimates]);

  const refresh = () => {
    axios.get(`${API}/estimates/all`, { withCredentials: true })
      .then(({ data }) => {
        setEstimates(data);
        setSelected((prev) => prev ? (data.find((e) => e.id === prev.id) || null) : prev);
      })
      .catch(console.error);
  };

  const loadDiag = () => axios.get(`${API}/admin/estimates/diagnostics`, { withCredentials: true })
    .then(({ data }) => { setDiag(data); setShowDiag(true); })
    .catch((e) => toast.error(e.response?.data?.detail || 'Diagnostics failed'));

  const loadAudit = () => axios.get(`${API}/admin/release-audit`, { withCredentials: true })
    .then(({ data }) => { setAudit(data); setShowAudit(true); })
    .catch((e) => toast.error(e.response?.data?.detail || 'Release audit failed'));

  const estDate = (e) => e.service_date || etDate(e.date);

  const rangeBounds = useMemo(() => {
    const t = todayET();
    const y = Number(t.slice(0, 4)); const m = Number(t.slice(5, 7)); const d = Number(t.slice(8, 10));
    const iso = (yy, mm, dd) => `${yy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
    if (range === 'today') return [t, t];
    if (range === 'week') { const dt = new Date(Date.UTC(y, m - 1, d)); const dow = dt.getUTCDay(); const start = new Date(dt); start.setUTCDate(d - dow); const end = new Date(start); end.setUTCDate(start.getUTCDate() + 6); return [etDate(start), etDate(end)]; }
    if (range === 'month') return [iso(y, m, 1), iso(y, m, 31)];
    if (range === 'year') return [iso(y, 1, 1), iso(y, 12, 31)];
    if (range === 'custom') return [customStart || '0000-00-00', customEnd || '9999-99-99'];
    return null; // all
  }, [range, customStart, customEnd]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const digits = ql.replace(/\D/g, '');
    return estimates.filter((e) => {
      if (rangeBounds) { const ed = estDate(e); if (ed < rangeBounds[0] || ed > rangeBounds[1]) return false; }
      if (techFilter !== 'all' && (e.technician_name || '') !== techFilter) return false;
      if (ql) {
        const hay = [e.customer_name, e.technician_name, e.estimate_number, e.work_order_number, e.description, e.notes, e.service_address].join(' ').toLowerCase();
        const phoneHit = digits && (e.customer_phone || '').replace(/\D/g, '').includes(digits);
        if (!hay.includes(ql) && !phoneHit) return false;
      }
      return true;
    });
  }, [estimates, rangeBounds, techFilter, q]);

  const counts = useMemo(() => {
    const c = { all: filtered.length };
    WORKFLOW.forEach((w) => { c[w.key] = 0; });
    filtered.forEach((e) => { const ws = e.workflow_status || 'draft'; c[ws] = (c[ws] || 0) + 1; });
    return c;
  }, [filtered]);

  const visible = useMemo(() => tab === 'all' ? filtered : filtered.filter((e) => (e.workflow_status || 'draft') === tab), [filtered, tab]);
  const summary = useMemo(() => ({ count: visible.length, total: visible.reduce((s, e) => s + (Number(e.amount) || 0), 0) }), [visible]);

  const techs = useMemo(() => Array.from(new Set(estimates.map((e) => e.technician_name).filter(Boolean))).sort(), [estimates]);

  const changeStatus = async (e, next) => {
    if (next === (e.workflow_status || 'draft')) return;
    let reason = '';
    if (REASON_REQUIRED.has(next)) {
      reason = window.prompt(`Reason to mark this estimate "${next}":`) || '';
      if (!reason.trim()) { toast.error('A reason is required.'); return; }
    }
    try {
      await axios.patch(`${API}/admin/estimates/${e.id}/workflow-status`, { status: next, reason }, { withCredentials: true });
      toast.success('Status updated');
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  const repair = async (row) => {
    const reason = window.prompt('Repair reason (required):') || '';
    if (!reason.trim()) { toast.error('A repair reason is required.'); return; }
    try {
      const { data } = await axios.post(`${API}/admin/estimates/repair/${row.completion_id}`, { reason }, { withCredentials: true });
      toast.success(data.already_created ? 'Estimate already existed (no duplicate created)' : `Created ${data.estimate_number}${data.amount_pending ? ' — Amount Pending' : ''}`);
      loadDiag(); refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Repair failed'); }
  };

  const statusColor = (s) => ({
    accepted: 'text-green-400 bg-green-500/10', declined: 'text-red-400 bg-red-500/10',
    voided: 'text-red-400 bg-red-500/10', converted: 'text-cyan-300 bg-cyan-500/10',
    needs_review: 'text-amber-300 bg-amber-500/10', on_hold: 'text-orange-300 bg-orange-500/10',
    sent: 'text-blue-300 bg-blue-500/10', viewed: 'text-blue-300 bg-blue-500/10',
  }[s] || 'text-[#A3A3A3] bg-[#262626]');

  return (
    <div data-testid="admin-estimates-page" className="space-y-4">
      <CustomerTabsBar userRole={user?.role} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1">All Estimates</h2>
          <p className="text-[#A3A3A3] text-sm">Track every estimate — created from the field or the office.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="est-diagnostics-btn" onClick={loadDiag} className="text-xs border border-amber-600/40 text-amber-300 rounded px-3 h-9 inline-flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Missing Estimates</button>
          <button data-testid="est-release-audit-btn" onClick={loadAudit} className="text-xs border border-cyan-600/40 text-cyan-300 rounded px-3 h-9 inline-flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Release Audit</button>
          {hasPermission('create_estimates') && (
            <BillingCreateSheet type="estimate" onCreated={(newDoc) => { refresh(); if (newDoc) { setSelected(newDoc); setEditOnOpen(true); } }} />
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <select data-testid="est-range" value={range} onChange={(e) => setRange(e.target.value)} className="bg-[#141414] border border-[#262626] rounded h-9 px-2 text-white text-sm">
          <option value="all">All dates</option><option value="today">Today</option><option value="week">This Week</option>
          <option value="month">This Month</option><option value="year">This Year</option><option value="custom">Custom</option>
        </select>
        {range === 'custom' && (
          <>
            <input data-testid="est-range-start" type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="bg-[#141414] border border-[#262626] rounded h-9 px-2 text-white text-sm" />
            <input data-testid="est-range-end" type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="bg-[#141414] border border-[#262626] rounded h-9 px-2 text-white text-sm" />
          </>
        )}
        <select data-testid="est-tech-filter" value={techFilter} onChange={(e) => setTechFilter(e.target.value)} className="bg-[#141414] border border-[#262626] rounded h-9 px-2 text-white text-sm">
          <option value="all">All technicians</option>
          {techs.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-[#525252] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="est-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, phone, address, estimate #, work order, description…" className="w-full bg-[#141414] border border-[#262626] rounded h-9 pl-9 pr-3 text-white text-sm" />
        </div>
      </div>

      {/* Status tabs */}
      <div className="flex flex-wrap gap-1.5">
        {[{ key: 'all', label: 'All' }, ...WORKFLOW].map((w) => (
          <button key={w.key} data-testid={`est-tab-${w.key}`} onClick={() => setTab(w.key)}
            className={`text-xs rounded px-2.5 h-8 border ${tab === w.key ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#262626] text-[#A3A3A3] hover:border-[#3f3f3f]'}`}>
            {w.label} <span className="opacity-70">({counts[w.key] || 0})</span>
          </button>
        ))}
      </div>

      {/* Summary */}
      <div data-testid="est-summary" className="bg-[#141414] border border-[#262626] rounded-md px-4 py-3 flex items-center justify-between">
        <span className="text-[#A3A3A3] text-sm">{summary.count} estimate{summary.count === 1 ? '' : 's'} in view</span>
        <span className="text-white text-sm">Total estimated value: <span className="text-[#D4AF37] font-bold">${summary.total.toFixed(2)}</span></span>
      </div>

      {/* Rows */}
      <div className="grid gap-3">
        {visible.length === 0 ? (
          <div className="bg-[#141414] border border-[#262626] rounded-md p-8 text-center text-[#A3A3A3]">No estimates match these filters</div>
        ) : visible.map((e) => (
          <div key={e.id} data-testid={`admin-estimate-${e.id}`} className="bg-[#141414] border border-[#262626] rounded-md p-4 hover:border-[#D4AF37]/60 transition-colors">
            <div className="flex items-start justify-between gap-4">
              <button onClick={() => setSelected(e)} className="flex gap-3 flex-1 text-left min-w-0">
                <ClipboardList className="w-5 h-5 text-[#D4AF37] mt-1 shrink-0" />
                <div className="min-w-0">
                  <p className="text-white font-medium truncate">{e.estimate_number} • {e.customer_name}
                    {e.created_from_job && <span data-testid={`est-fromjob-${e.id}`} className="ml-2 text-[10px] font-bold bg-cyan-900/40 text-cyan-300 px-1.5 py-0.5 rounded">FROM JOB</span>}
                    {e.amount_pending && <span className="ml-2 text-[10px] font-bold bg-amber-900/40 text-amber-300 px-1.5 py-0.5 rounded">AMOUNT PENDING</span>}
                    {e.customer_release_status === 'approved_for_customer'
                      ? <span data-testid={`est-release-${e.id}`} className="ml-2 text-[10px] font-bold bg-green-900/40 text-green-300 px-1.5 py-0.5 rounded">VISIBLE TO CUSTOMER</span>
                      : <span data-testid={`est-release-${e.id}`} className="ml-2 text-[10px] font-bold bg-[#404040] text-[#A3A3A3] px-1.5 py-0.5 rounded">HIDDEN</span>}
                  </p>
                  <p className="text-[#A3A3A3] text-xs mt-0.5 truncate">{e.description || e.title || e.line_items?.[0]?.description || ''}</p>
                  <p className="text-[#525252] text-xs mt-0.5">
                    {e.technician_name ? `${e.technician_name} · ` : ''}{e.work_order_number ? `WO ${e.work_order_number} · ` : ''}{estDate(e)}{e.follow_up_date ? ` · follow-up ${e.follow_up_date}` : ''}
                  </p>
                </div>
              </button>
              <div className="text-right shrink-0">
                <p className="text-[#D4AF37] text-xl font-bold">{e.amount_pending ? 'Pending' : `$${(Number(e.amount) || 0).toFixed(2)}`}</p>
                {canEdit ? (
                  <select data-testid={`est-status-${e.id}`} value={e.workflow_status || 'draft'} onChange={(ev) => changeStatus(e, ev.target.value)}
                    className={`mt-2 text-xs rounded px-2 h-7 border border-[#262626] bg-[#0A0A0A] ${statusColor(e.workflow_status)}`}>
                    {WORKFLOW.map((w) => <option key={w.key} value={w.key}>{w.label}</option>)}
                  </select>
                ) : (
                  <span className={`inline-block mt-2 px-2 py-1 rounded text-xs ${statusColor(e.workflow_status)}`}>{(e.workflow_status || 'draft').replace(/_/g, ' ').toUpperCase()}</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Diagnostics / repair drawer */}
      {showDiag && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={() => setShowDiag(false)}>
          <div className="w-full max-w-xl bg-[#0A0A0A] border-l border-[#262626] h-full overflow-y-auto p-5" onClick={(ev) => ev.stopPropagation()} data-testid="est-diag-drawer">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white text-lg font-semibold">Estimate-Only History Diagnostic</h3>
              <button onClick={() => setShowDiag(false)} className="text-[#525252] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            {!diag ? <p className="text-[#A3A3A3]">Loading…</p> : (
              <>
                <div className="flex flex-wrap gap-2 mb-4 text-xs">
                  {Object.entries(diag.counts || {}).map(([k, v]) => (
                    <span key={k} className="px-2 py-1 rounded bg-[#141414] border border-[#262626] text-[#A3A3A3]">{k.replace(/_/g, ' ')}: <span className="text-white">{v}</span></span>
                  ))}
                  {(!diag.items || diag.items.length === 0) && <span className="text-[#A3A3A3]">No estimate-only completions found.</span>}
                </div>
                <div className="space-y-2">
                  {(diag.items || []).map((row) => (
                    <div key={row.completion_id} data-testid={`est-diag-row-${row.completion_id}`} className="bg-[#141414] border border-[#262626] rounded p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-white text-sm font-medium">{row.customer_name || 'Unknown'} {row.estimate_number ? `· ${row.estimate_number}` : ''}</p>
                          <p className="text-[#A3A3A3] text-xs">{row.technician_name || '—'} · WO {row.work_order_number || '—'} · {row.service_date || '—'}</p>
                          <p className="text-[#525252] text-xs mt-1 line-clamp-2">{row.submitted_scope || 'No saved scope'}</p>
                          <p className={`text-xs mt-1 ${row.classification === 'missing' ? 'text-red-300' : row.classification === 'linked' ? 'text-green-400' : 'text-amber-300'}`}>{row.classification.replace(/_/g, ' ')} — {row.root_cause}</p>
                        </div>
                        {row.repairable && (
                          <button data-testid={`est-repair-${row.completion_id}`} onClick={() => repair(row)} className="shrink-0 text-xs border border-[#D4AF37]/40 text-[#D4AF37] rounded px-2.5 h-8 inline-flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5" /> Create Missing Estimate</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <BillingDetailSheet
        open={!!selected}
        onOpenChange={(o) => { if (!o) { setSelected(null); setEditOnOpen(false); } }}
        doc={selected}
        type="estimate"
        viewerRole="admin"
        editOnOpen={editOnOpen}
        onChange={refresh}
      />

      {/* Release audit (read-only) — invoices + estimates */}
      {showAudit && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={() => setShowAudit(false)}>
          <div className="w-full max-w-xl bg-[#0A0A0A] border-l border-[#262626] h-full overflow-y-auto p-5" onClick={(ev) => ev.stopPropagation()} data-testid="est-audit-drawer">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white text-lg font-semibold">Customer-Release Audit (read-only)</h3>
              <button onClick={() => setShowAudit(false)} className="text-[#525252] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-[#A3A3A3] text-xs mb-4">Nothing here is changed automatically. Open a record to Approve &amp; Release it individually.</p>
            {!audit ? <p className="text-[#A3A3A3]">Loading…</p> : ['estimates', 'invoices'].map((k) => (
              <div key={k} className="mb-5">
                <p className="text-white text-sm font-semibold capitalize mb-2">{k} — {audit[k].count} total</p>
                <div className="flex flex-wrap gap-2 mb-2 text-xs">
                  {Object.entries(audit[k].counts || {}).map(([c, v]) => (
                    <span key={c} className={`px-2 py-1 rounded border ${c === 'properly_approved' ? 'border-green-700/40 text-green-300' : c.includes('visible') && c !== 'properly_approved' ? 'border-red-700/40 text-red-300' : 'border-[#262626] text-[#A3A3A3]'}`}>{c.replace(/_/g, ' ')}: <span className="text-white">{v}</span></span>
                  ))}
                </div>
                {audit[k].currently_visible.length > 0 ? (
                  <div className="space-y-1">
                    <p className="text-red-300 text-xs">Currently visible to customer:</p>
                    {audit[k].currently_visible.map((r) => (
                      <p key={r.id} className="text-[#A3A3A3] text-xs">{r.number} · {r.customer_name} · ${r.amount?.toFixed?.(2)} · {r.classification.replace(/_/g, ' ')}</p>
                    ))}
                  </div>
                ) : <p className="text-green-400 text-xs">None currently visible without proper approval.</p>}
                <p className="text-[#525252] text-xs mt-1">{audit[k].legacy.length} legacy record(s) with no release status (hidden under strict gate).</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEstimates;
